<template lang="pug">
.product-price(:class="theme")
  .price(v-if="!onlyExclVat" :itemprop="schemaPrice ? 'price' : undefined" :content="schemaPrice && typeof displayedPrice === 'number' ? displayedPrice : undefined") {{ rogue.currency(displayedPrice, { allowLocalCurrency: theme !== 'checkout' }) }}
    span(v-if="isProgram && theme !== 'wishlist' && theme !== 'checkout'") {{ subscriptionInterval.toUpperCase() }}
  .single-price(v-if="showSinglePrice") @ {{ rogue.currency(singlePriceInclVat, { allowLocalCurrency: theme !== 'checkout' }) }}{{subscriptionInterval}}
  .old-price(v-if="specialPrice !== undefined && !hideNormalPrice && mainProduct?.price") {{ rogue.currency(mainProduct.price, { euTaxClass: taxClass }) }}
  .old-price(v-else-if="slashedBundlePrice && !hideNormalPrice") {{rogue.currency(slashedBundlePrice, { euTaxClass: taxClass })}}
  div(:class="(specialPrice !== undefined) ? 'old-price' : 'price'")
  .break(v-if="theme && ['category', 'search', 'list'].includes(theme)")
  .excluding-tax-container(v-if="taxClass && showExclVat && singlePrice < singlePriceInclVat")
    span.tax-label {{theme && ['category','list'].includes(theme) ? __('Excluding VAT') : __('Excl. VAT') }}
    .tax-excl {{ rogue.currency(showTotalPrice ? totalPrice : singlePrice) }}
</template>
<script setup lang="ts">
import { computed, type PropType, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { RogueOrder } from '@orders'
import { type TaxClass, getPriceInclTax } from '@rogue/universal'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useProductStore } from '@/js/stores/productStore'
import { usePromoStore } from '@/js/stores/promoStore'
import { useRogue } from '@/js/services/Rogue'
import { computePriceSync } from '@/js/helpers/price-utils'
import { getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import type { BuyRequest, HotDeal, Product } from '@/js/services/RogueTypes'

let getOrder: (() => RogueOrder | null) | undefined

const props = defineProps({
  product: {
    type     : Object as PropType<Product>,
    required : false,
  },
  buyRequest: {
    type     : Object as PropType<BuyRequest>,
    required : false,
  },
  showTotalPrice: {
    type    : Boolean,
    default : true,
  },
  showSinglePrice: {
    type    : Boolean,
    default : false,
  },
  hideNormalPrice: {
    type    : Boolean,
    default : false,
  },
  showExclVat: {
    type    : Boolean,
    default : false,
  },
  onlyExclVat: {
    type    : Boolean,
    default : false,
  },
  theme: {
    type     : String,
    required : false,
  },
})

const rogue = useRogue()
const customerStore = useCustomerStore()
const productStore = useProductStore()
const promoStore = usePromoStore()

watch(() => rogue.loaded.checkout, (v) => {
  if (v) import('@/js/pages/checkout/checkout').then(m => { getOrder = m.getOrder })
})

const activePromos = computed(() => promoStore.activePromos)
const customerPrices = computed(() => promoStore.customerPrices)
const products = computed(() => productStore.products || [])
const earnedProducts = computed(() => customerStore.activeEarnedProducts || [])

const mainProduct = computed<Product | undefined>(() => {
  if (props.product) {
    return props.product
  }

  let buyRequest = props.buyRequest

  let product = buyRequest?.productId ? products.value[buyRequest.productId] || undefined : undefined
  while (buyRequest?.children?.length) {
    // If this is a bundle or if it has > 1 qty we need to just compute the buy request's quantity
    if (!product || Object.values(buyRequest.options || {}).length || buyRequest.children.length > 1) {
      return undefined
    }

    buyRequest = buyRequest.children[0]
    product = products.value[buyRequest.productId]
  }

  return product || undefined
})

const isProgram = computed(() => mainProduct.value?.isProgram)

const taxClass = computed<TaxClass | undefined>(() => {
  if (!['EUR', 'GBR'].includes(rogue.site.code)) {
    return undefined
  }

  let taxClassVal = mainProduct.value?.attributes?.tax_class_id

  if (!taxClassVal && props.buyRequest) {
    // If the main product has no tax class then find the first simple
    taxClassVal = getSimpleForBuyRequest(props.buyRequest, rogue)?.attributes?.tax_class_id
  }

  if (!taxClassVal && props.product?.productId) {
    taxClassVal = products.value[props.product?.productId]?.attributes.tax_class_id
  }

  return taxClassVal as TaxClass
})

const subscriptionInterval = computed<string>(() => {
  // @todo for config products?
  if (isProgram.value && mainProduct.value?.attributes?.subscription_interval) {
    if (mainProduct.value?.attributes?.subscription_interval === 'Month') {
      return '/mo'
    }

    return `/${mainProduct.value.attributes.subscription_interval}`
  }

  return ''
})

const slashedBundlePrice = computed<number | null>(() => {
  const product = props.buyRequest?.productId
    ? products.value[props.buyRequest.productId] || null
    : null

  if (product?.type !== "bundle") return null

  // An unselected list-drawer child has qty 0, which prices to 0 and would hide the slashed price
  const buyRequest = { ...props.buyRequest!, qty: props.buyRequest!.qty || 1 }

  try {
    const regularPrice = computePriceSync(buyRequest, rogue.pinia!, { ignorePromo: true })
    const currentPrice = computePriceSync(buyRequest, rogue.pinia!)

    if (currentPrice && currentPrice < regularPrice) return regularPrice
  } catch (e) {
    return null
  }

  return null
})

const specialPrice = computed(() => {
  const product: (Product & {childId?: number, variationKey?: string}) | undefined = mainProduct.value
  if (!product?.productId) {
    return undefined
  }

  let promoPrice
  if (product.hasPromo && product.price === -100) {
    // Just necessary to watch reactive hasPromo property...
    rogue.log.info('promo_watch_1')
  }

  let promo: HotDeal | undefined = activePromos.value[product.productId]
  if (product.variationKey) {
    promo = activePromos.value[product.productId]?.variations?.find(v => v.key === product.variationKey) as HotDeal | undefined
    if (!promo) {
      const variationId = parseInt(product.variationKey, 10)
      if (!isNaN(variationId)) promo = activePromos.value[variationId]
    }
  } else if (product.childId) {
    promo = activePromos.value[product.childId]
  }
  if (promo && promo.status === 'active') {
    promoPrice = promo.price
  }

  const specialPrices = [promoPrice, customerPrices.value[product.productId], product.special_price]
    .filter(val => (val === 0 || !!val) && product.price && Number(val) < product.price) as number[]

  const min = specialPrices.length ? Math.min(...specialPrices) : undefined

  const { value, type } = earnedProducts.value[product.productId] || {}
  if (value) {
    const normalPrice = min || product.price
    if (normalPrice) {
      if (type === 'fixed') {
        return normalPrice - value
      }

      return normalPrice - ((value / 100) * normalPrice)
    }
  }

  return specialPrices.length ? min : undefined
})

const singlePrice = computed<string | number>(() => {
  // This is required just to make this function watch the reactive properties... it will never be true :(
  if (
    mainProduct.value?.hasPromo
      || (mainProduct.value?.productId && activePromos.value[mainProduct.value?.productId]?.price < -100)
      || (mainProduct.value?.special_price && mainProduct.value?.special_price < -100)
  ) {
    if (mainProduct.value.price === -100) {
      rogue.log.info('promo_watch')
    }
  }
  let price: string | number = '--'

  if (props.buyRequest) {
    try {
      price = computePriceSync({ ...props.buyRequest, qty: 1 }, rogue.pinia!)
    } catch (e) {
      //
    }
  } else if (specialPrice.value !== undefined && specialPrice.value >= 0) {
    price = specialPrice.value
  } else {
    price = mainProduct.value?.price || price
  }

  return price
})

const singlePriceInclVat = computed(() => {
  if (!['EUR', 'GBR'].includes(rogue.site.code)) {
    return singlePrice.value
  }

  if (rogue.loaded.checkout && getOrder?.()?.state.tax.isTaxExempt) {
    return singlePrice.value
  }

  if (typeof singlePrice.value === 'string') {
    return singlePrice.value
  }

  return getPriceInclTax(singlePrice.value, taxClass.value, rogue.l10n.country || undefined)
})

const totalPrice = computed(() => {
  if (typeof singlePrice.value === 'string') {
    return singlePrice.value
  }

  return (props.buyRequest?.qty || 1) * singlePrice.value
})

const displayedPrice = computed(() => props.showTotalPrice ? totalPriceInclVat.value : singlePriceInclVat.value)

const route = useRoute()

const schemaPrice = computed(() =>
  (props.theme === 'rhpa-child' || props.theme === 'rhpa-child condensed') &&
  !!route.query.sku &&
  mainProduct.value?.sku === route.query.sku &&
  (mainProduct.value?.inventory?.bin || mainProduct.value?.inventory?.max === 0)
)

const totalPriceInclVat = computed(() => {
  if (!['EUR', 'GBR'].includes(rogue.site.code)) {
    return totalPrice.value
  }

  if (typeof singlePriceInclVat.value === 'string') {
    return singlePriceInclVat.value
  }

  return (props.buyRequest?.qty || 1) * singlePriceInclVat.value
})
</script>

<style lang="scss" scoped>
.break {
  height: 0;
  flex-basis: 100%;
}

.price {
  font-size: $size-xs;
}

.excluding-tax-container {
  color: $color-gray-x-dark;
  font-size: $size-xxs;
  line-height: $line-height-s;
  display: flex;

  @include mobile(){
    flex-wrap: wrap;
  }
}

.tax-label {
  margin-right: $space-xxxs;
  font-weight: $weight-light;
}

.price, .tax-excl {
  font-weight: $weight-bold;
}

// Themes
.product-price {
  &.category, &.list {
    .old-price {
      margin-left: $space-xxxs;
    }
  }

  &.category {
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .excluding-tax-container {
      margin-top: $space-xxs;
    }
  }

  &.rhpa, &.rhpa-child, &.search, &.side-cart {
    .excluding-tax-container {
      flex-direction: column;
      font-style: italic;

      .tax-label {
        margin-right: 0;
      }
    }
  }

  &.rhpa, &.rhpa-child {
    .excluding-tax-container {
      align-items: flex-end;
    }
  }

  &.rhpa, &.rhpa-child, &.search, &.side-cart, &.list {
    .excluding-tax-container {
      margin-top: $space-xxxs;
    }
  }

  &.calculated-options {
    .price {
      font-size: $size-m;
    }
  }

  &.grouped-total {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .price {
      font-size: $size-l;
      font-family: $font-condensed;
    }

    .excluding-tax-container {
      display: flex;
      flex-direction: column;
      width: min-content;
    }

    // Full-width totals keep the slashed price beside the total instead of at the far edge
    &.compact {
      justify-content: flex-start;
      gap: $space-xxs;
    }
  }

  // In a grouped product, the price displays differently
  &.rhpa-child {
    min-width: 82px;
    max-width: 82px;
    margin-right: $space-s;
    text-align: right;
    font-weight: $weight-bold;
    display: flex;
    flex-direction: column;
    align-content: center;
    margin-bottom: 0;

    .price {
      font-size: $size-m;
    }

    .old-price {
      color: $color-black;
      font-size: $size-xs;
      font-weight: $weight-light;
      line-height: 1em;
      margin-top: $space-xxs;
    }

    &.condensed .price {
      font-size: $size-s;
      font-family: $font-condensed;
      line-height: $line-height-s;
    }
    &.accessories {
      margin-top: 8px;
      text-align: left;
    }
  }

  &.rhpa {
    display: flex;
    align-items: center;
    margin-bottom: $space-xxs;
    font-size: $size-l;

    .old-price {
      margin-left: $space-xs;
      color: $color-black-light;
      font-size: $size-s;
      text-decoration: line-through;
    }

    .price {
      font-family: $font-condensed;
      line-height: $line-height-s;
      font-weight: $weight-bold;
      font-size: $size-xl;
    }

    &.inline {
      flex-direction: column;
      margin-bottom: 0;
      align-items: flex-end;
      margin-right: $space-s;

      .old-price {
        margin-left: 0;
      }
    }

    .excluding-tax-container {
      margin-left: $space-xs;
    }
  }

  &.search, &.side-cart {
    flex-wrap: wrap;

    .price {
      font-size: $size-xxs;
    }

    .old-price {
      margin-left: $space-xxxs;
    }
  }

  &.side-cart {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .old-price {
      display: block;
      margin-left: 0;
    }
  }
}

.wishlist, .checkout {
  display: block;
  margin-left: $size-s;

  .old-price {
    display: flex;
    flex-direction: row-reverse;
  }

  .price {
    font-weight: $weight-bold;
    font-size: $size-s;
  }

  .single-price {
    display:flex;
    font-size: $size-xxs;
  }

  .excluding-tax-container {
    color: #666666;
    font-size: 12px;
    line-height: 1;
    display: block;
  }

  .tax-excl {
    display:flex;
    flex-direction: row-reverse;
  }

  .product-price.rhpa-child {
    min-width: 118px;
  }
}

.product-price {
  color: $color-black;
}

.old-price {
  font-size: $size-xxs;
  color: $color-gray-dark;
  text-decoration: line-through;
}

</style>
